import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {BasicTableProps} from 'notebook/structured-tables/StructuredTableConstants';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import TransferTableConfigComponent from 'notebook/transfer-tables/TransferTableConfig';
import {TransferTableConfig} from 'notebook/transfer-tables/TransferTableConstants';
import {mergeTableUpdateIntoTransferTableConfig} from 'notebook/transfer-tables/TransferTableNotePartUtil';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import APIUtil from 'util/APIUtil';
import {MakeFieldsRequired} from 'util/Types';

function doesUserHavePermissionToViewTable(
  item: TransferTableConfig
): item is TransferTableConfig & {containerSchema: StorableSchema} {
  return item.containerSchema != null;
}

const TRANSFER_TABLE_CONFIG_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.TRANSFER_TABLE_CONFIG,
  typeClass: 'is-table',
  title: 'Transfer Table',
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    getItemProps(item: NotePart & {item: TransferTableConfig}, index: number): TableItemProps {
      return {
        ...MediocrePlugins.getTableItemProps.call(this, item, index),
      };
    },
    getIsCollapsed(
      item: NotePart & {item: TransferTableConfig},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      // Component doesn't enable collapsing.
      return false;
    },
    getIsInaccessible(item: NotePart & {item: TransferTableConfig}): boolean {
      return !doesUserHavePermissionToViewTable(item.item);
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: TransferTableConfig},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: TableItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const transferTableConfig: TransferTableConfig = item.item;
      const {name, parentStorableIsGrid, columns, provenanceUuid} = transferTableConfig;
      const sharedProps: MakeFieldsRequired<BasicTableProps, 'tableTypeLabel'> = {
        icon: 'transfer',
        indentation: this.getIndentation(item),
        tableTypeLabel: 'Transfer',
        tableName: name,
      };
      if (!doesUserHavePermissionToViewTable(transferTableConfig)) {
        return <TableWithMissingSchema {...sharedProps} />;
      }
      return (
        <TransferTableConfigComponent
          {...sharedProps}
          {...itemProps}
          fileId={editorProps.fileId}
          containerSchema={transferTableConfig.containerSchema}
          parentStorableIsGrid={parentStorableIsGrid}
          structuredTableColumns={columns}
          provenanceUuid={provenanceUuid as string}
          editor={this}
          editorProps={editorProps}
          // Standard table props
          cid={cid}
          index={index}
          mouseDownHandler={this.itemMouseDownHandler}
          mouseOverHandler={this.itemMouseOverHandler}
          updateHandler={this.itemUpdateHandler}
          {...searchResultProps}
        />
      );
    },
  },
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: TransferTableConfig}): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: TransferTableConfig}): NotePart | null {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    const sourceFileType = APIUtil.apiIdToType(
      $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID)
    );
    if ([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY].includes(fileType)) {
      notifications.warn('Cannot paste transfer tables from template to entry.');
      return null;
    } else if (fileType !== sourceFileType) {
      notifications.warn('Cannot paste transfer table.');
      return null;
    } else if (
      TransferTableUtil.getTransferTableConfigsInNote(this.props.note)
        .map((transferTableConfig) => transferTableConfig.provenanceUuid)
        .includes(item.item.provenanceUuid)
    ) {
      notifications.warn('Cannot create more than one copy of the same transfer table.');
      return null;
    } else {
      return item;
    }
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: TransferTableConfig}): string {
    return item.name;
  },
  getContent(this: NoteEditor, {item}: NotePart & {item: TransferTableConfig}): {table: Table} {
    return {
      table: TransferTableUtil.createTableForConfig(
        item.name,
        item.columns,
        item.containerSchema,
        item.parentStorableIsGrid,
        item.provenanceUuid
      ),
    };
  },
  setContent({item, table}: {item?: TransferTableConfig; table?: TableData}): Spec<NotePart> {
    if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(transferTableConfig: TransferTableConfig): TransferTableConfig {
            return mergeTableUpdateIntoTransferTableConfig(transferTableConfig, table);
          },
        },
      };
    }

    assert(false, 'setContent invoked without content to set.');
  },
  getStoreDependenciesForSpreadsheetEval(item: NotePart & {item: TransferTableConfig}): StoreDependencyMap {
    return {};
  },
  *getSearchResults(
    item: NotePart & {item: TransferTableConfig},
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null
  ): Generator<MediocreSearchResult> {
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, item.item.name, item.cid);

    // Search over the column names
    const columnNames = item.item.columns.map((column) => column.name ?? '');
    yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

    const computedTable = computedTables?.[item.item.name];
    const i = 0; // TransferTabelConfig is a single row comprised of the defaultValue on each column
    for (const [j, column] of item.item.columns.entries()) {
      if (
        doesTableCellMatch(
          matcher,
          column.defaultValue,
          computedTable ? computedTable[i][j] : null,
          searchOverFormulas
        )
      ) {
        yield buildTableSearchResult(item.cid, i, j);
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default TRANSFER_TABLE_CONFIG_PLUGIN;
